/**
 * User Context - Maneja el usuario actual para testing
 * Sincroniza con TestingContext para mantener consistencia
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MOCK_USERS, MockUser, DEFAULT_TEST_USER } from '../mocks/mockUsers';
import { useTesting } from './TestingContext';

interface UserContextType {
  currentUser: MockUser;
  setCurrentUser: (user: MockUser) => void;
  resetToDefaultUser: () => void;
  isPresident: boolean;
  isManager: boolean;
  isAgent: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { userRole, setUserRole, owlyMode } = useTesting();

  // Usuario por defecto de OWLY (Pablo González - CEO)
  const DEFAULT_OWLY_USER = MOCK_USERS.find(u => u.id === 'user-owly-ceo') || MOCK_USERS[0];

  // Inicializar con usuario guardado en localStorage o DEFAULT_TEST_USER (Patricia Morales de Los Andes)
  // IMPORTANTE: Si el usuario guardado es de OWLY interno (sin companyId), usar DEFAULT_TEST_USER
  const getInitialUser = () => {
    const savedUserId = localStorage.getItem('owly-current-user-id');
    if (savedUserId) {
      const savedUser = MOCK_USERS.find(u => u.id === savedUserId);
      // Solo usar el usuario guardado si tiene companyId (es cliente, no OWLY interno)
      if (savedUser && savedUser.companyId) {
        return savedUser;
      }
    }
    return DEFAULT_TEST_USER;
  };

  const [currentUser, setCurrentUser] = useState<MockUser>(getInitialUser);

  // Cambiar automáticamente el usuario cuando cambie el OWLY Mode
  useEffect(() => {
    if (owlyMode) {
      // Modo OWLY activado: cambiar a Pablo González (CEO)
      setCurrentUser(DEFAULT_OWLY_USER);
      setUserRole(DEFAULT_OWLY_USER.role);
    } else {
      // Modo Company activado: volver al usuario de compañía
      const companyUser = getInitialUser();
      setCurrentUser(companyUser);
      setUserRole(companyUser.role);
    }
  }, [owlyMode]);

  // Persistir usuario en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('owly-current-user-id', currentUser.id);
    // Mantener sincronizado el rol en TestingContext
    if (userRole !== currentUser.role) {
      setUserRole(currentUser.role);
    }
  }, [currentUser.id]);

  // Exponer función de reset en window para debugging desde consola
  useEffect(() => {
    (window as any).resetUser = () => {
      localStorage.setItem('owly-current-user-id', DEFAULT_TEST_USER.id);
      window.location.reload();
    };
    return () => {
      delete (window as any).resetUser;
    };
  }, []);

  // Sincronizar cuando cambie el usuario manualmente
  const handleSetCurrentUser = (user: MockUser) => {
    setCurrentUser(user);
    localStorage.setItem('owly-current-user-id', user.id);
    // Actualizar TestingContext con el nuevo rol
    setUserRole(user.role);
  };

  // Reset al usuario por defecto
  const resetToDefaultUser = () => {
    localStorage.setItem('owly-current-user-id', DEFAULT_TEST_USER.id);
    setCurrentUser(DEFAULT_TEST_USER);
    setUserRole(DEFAULT_TEST_USER.role);
  };

  const isPresident = currentUser.role === 'president';
  const isManager = currentUser.role === 'manager';
  const isAgent = currentUser.role === 'agent';

  return (
    <UserContext.Provider
      value={{
        currentUser,
        setCurrentUser: handleSetCurrentUser,
        resetToDefaultUser,
        isPresident,
        isManager,
        isAgent,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
